import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaperMap, ValidationDiagnostic } from '@/lib/writer/structured';
import { splitWriterDiagnostics } from '@/lib/writer/diagnosticSeverity';

interface Props {
  paperMap: PaperMap | null;
  diagnostics: ValidationDiagnostic[];
  loading: boolean;
}

/**
 * Compact structural outline of the current draft — reads directly from the
 * Phase 3 structured tables so it stays in lockstep with what the agent
 * actually edits, not what happens to be embedded in the last compiled TeX.
 *
 * Doubles as a lint surface: pre-compile diagnostics (missing labels,
 * unresolved refs, unresolved cites) roll up here so authors don't have to
 * wait for BibTeX to fail before spotting a typo.
 */
const WriterPaperMapPanel: React.FC<Props> = ({
  paperMap,
  diagnostics,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [problemsOpen, setProblemsOpen] = useState(false);

  const sections = paperMap?.sections ?? [];
  const { problems, warnings } = splitWriterDiagnostics(diagnostics);
  const structuralWarnings = warnings.filter((d) => d.code !== 'uncited_bib_entry');
  const problemCount = problems.length;
  const warningCount = structuralWarnings.length;

  return (
    <div className="border-b border-border/60 bg-background/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/40 transition"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Paper map
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          <span>{sections.length} sec</span>
          {problemCount > 0 ? (
            <span className="text-destructive">· {problemCount} need fixing</span>
          ) : warningCount > 0 ? (
            <span className="text-warning-foreground">· {warningCount} warning{warningCount === 1 ? '' : 's'}</span>
          ) : sections.length > 0 ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
          ) : null}
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-2">
          {sections.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              No structured sections yet. Ask the agent to draft a section — it
              will land here as soon as you accept the artifact.
            </p>
          ) : (
            <ul className="text-sm space-y-0.5">
              {sections.map((s) => {
                const sectionProblems = problems.filter(
                  (d) => d.anchor_id === s.anchor_id,
                );
                const sectionWarnings = structuralWarnings.filter(
                  (d) => d.anchor_id === s.anchor_id,
                );
                return (
                  <li
                    key={s.anchor_id}
                    className="group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted/50"
                  >
                    <span className="text-[10px] tabular-nums w-5 text-muted-foreground">
                      {s.order_index + 1}
                    </span>
                    <span className="truncate flex-1 text-foreground/90">
                      {s.title || s.anchor_id}
                    </span>
                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                      {formatBytes(s.byte_size)}
                    </span>
                    {sectionProblems.length > 0 ? (
                      <span
                        title={sectionProblems.map((d) => d.message).join('\n')}
                        className="inline-flex items-center gap-1 text-destructive text-[10px]"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {sectionProblems.length}
                      </span>
                    ) : sectionWarnings.length > 0 ? (
                      <span
                        title={sectionWarnings.map((d) => d.message).join('\n')}
                        className="inline-flex items-center gap-1 text-warning-foreground text-[10px]"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {sectionWarnings.length}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {problemCount + warningCount > 0 ? (
            <div
              className={cn(
                'mt-2 rounded-md border',
                problemCount > 0
                  ? 'border-destructive/30 bg-destructive/5'
                  : 'border-warning/35 bg-warning/10',
              )}
            >
              <button
                type="button"
                onClick={() => setProblemsOpen((v) => !v)}
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium',
                  problemCount > 0 ? 'text-destructive' : 'text-warning-foreground',
                )}
              >
                {problemsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <AlertTriangle className="h-3 w-3" />
                {problemCount > 0
                  ? `${problemCount} need fixing`
                  : `${warningCount} warning${warningCount === 1 ? '' : 's'}`}
              </button>
              {problemsOpen ? (
                <ul
                  className={cn(
                    'px-3 pb-2 space-y-1 text-xs max-h-40 overflow-y-auto',
                    problemCount > 0 ? 'text-destructive/90' : 'text-warning-foreground',
                  )}
                >
                  {(problemCount > 0 ? problems : structuralWarnings).map((d, i) => (
                    <li
                      key={`${d.code}-${d.anchor_id ?? ''}-${d.target ?? ''}-${i}`}
                      className={cn('flex items-start gap-1.5')}
                    >
                      <span className="opacity-60 shrink-0 tabular-nums">
                        {codeLabel(d.code)}
                      </span>
                      <span className="min-w-0">{d.message}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  return `${(n / 1024).toFixed(1)}KB`;
}

function codeLabel(code: ValidationDiagnostic['code']): string {
  switch (code) {
    case 'missing_label':
      return 'label';
    case 'unresolved_ref':
      return 'ref';
    case 'unresolved_cite':
      return 'cite';
    case 'uncited_bib_entry':
      return 'uncited';
    case 'duplicate_anchor':
      return 'dup-anchor';
    case 'duplicate_label':
      return 'dup-label';
  }
}

export default WriterPaperMapPanel;
